import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, UserPlus, MailCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await register(form);
      setDone(true);
      toast.success("Account created!", { description: "We've sent a verification email — please check your inbox." });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[calc(100vh-110px)] flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md text-center bg-white rounded-3xl border border-slate-200 p-10 shadow-soft" data-testid="register-success">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5"><MailCheck className="w-8 h-8 text-emerald-600" /></div>
          <h1 className="font-serif text-3xl text-brand-navy tracking-tight">Verify your email</h1>
          <p className="mt-3 text-slate-600">We've sent a verification link to <strong className="text-brand-navy">{form.email}</strong>. Click it to activate your account before submitting applications.</p>
          <p className="mt-2 text-sm text-slate-400">Didn't get it? Check spam, or resend from your dashboard.</p>
          <button onClick={() => nav("/dashboard")} data-testid="register-continue-btn"
            className="mt-6 w-full px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors">Continue to dashboard</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-110px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-brand-navy grain">
        <img src="https://images.unsplash.com/photo-1547024842-a0e3d2127406?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Hanoi, Vietnam" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src="/logo.png" alt="ImmiLaw.VN" className="h-12 w-auto bg-white rounded-lg p-1.5 self-start" />
          <div>
            <h2 className="font-serif text-4xl tracking-tight leading-tight">Create your account to begin</h2>
            <p className="mt-4 text-slate-300 max-w-md">Registration is free. Upload your passport once and our AI fills your application forms automatically.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <h1 className="font-serif text-4xl text-brand-navy tracking-tight">Create account</h1>
          <p className="text-slate-500 mt-2">Register to apply for Vietnam immigration services.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Full name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="Nguyen Van A" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Phone (optional)</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-brand-red transition-colors" placeholder="+84 ..." />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Password</label>
              <div className="relative mt-1.5">
                <input required type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 outline-none focus:border-brand-red transition-colors" placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />} Create account
            </button>
          </form>

          <GoogleButton label="Sign up with Google" />

          <p className="mt-6 text-sm text-slate-500">Already have an account? <Link to="/login" className="font-semibold text-brand-red hover:underline" data-testid="register-to-login">Sign in</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
